import { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { StepLayout } from "../components/StepLayout";
import { getMerchantStepPath } from "../constants";
import { cn } from "@/utils/classnames";
import { Check } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { usePaymentMethods } from "@/queries/payment-method/use-payment-methods";
import { useAuth } from "@/providers/auth";
import { useApplicationId } from "../hooks/use-application-id";
import { useFees } from "@/queries/provider-onboarding/use-fees";
import { useTermsAndConditions } from "@/queries/provider-onboarding/use-terms-and-conditions";
import { useAcceptFees } from "@/queries/provider-onboarding/use-accept-fees";
import { useAcceptTermsAndConditions } from "@/queries/provider-onboarding/use-accept-terms-and-conditions";
import { useSubmitApplication } from "@/queries/provider-onboarding/use-submit-application";
import { useApplicationInfo } from "@/queries/provider-onboarding/use-application-info";
import { useDocuments } from "@/queries/provider-onboarding/use-documents";
import { APPLICATION_STATUS } from "@/queries/provider-onboarding/application-status";
import { PROVIDER_TYPE, MERCHANT_EXIT_PATH } from "../constants";
import { FeeSection } from "@/queries/provider-onboarding/types";
import { Download, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button-tw";
import { toast } from "react-toastify";

type TopTab = "rates" | "funding" | "terms";

const TOP_TABS: { key: TopTab; label: string[] }[] = [
  { key: "rates", label: ["Rates & Fees"] },
  { key: "funding", label: ["Funding & Fee Accounts"] },
  { key: "terms", label: ["Merchant Processing", "Terms & Conditions"] },
];

const FEE_ACCOUNT_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

const getFeeAccountFromStorage = (userId: string): string => {
  try {
    const raw = localStorage.getItem(`pb_fee_account_${userId}`);
    if (!raw) return "";
    const { value, expires } = JSON.parse(raw);
    if (Date.now() > expires) {
      localStorage.removeItem(`pb_fee_account_${userId}`);
      return "";
    }
    return value;
  } catch {
    return "";
  }
};

const saveFeeAccountToStorage = (userId: string, value: string) => {
  localStorage.setItem(
    `pb_fee_account_${userId}`,
    JSON.stringify({ value, expires: Date.now() + FEE_ACCOUNT_TTL_MS }),
  );
};

const extractTitle = (html: string): string => {
  const decoder = document.createElement("textarea");
  decoder.innerHTML = html;
  const decoded = decoder.value;
  const match = decoded.match(/<b[^>]*>(.*?)<\/b>/i);
  if (match) {
    decoder.innerHTML = match[1];
    return decoder.value.trim();
  }
  return "";
};

const PROCESSING_ACCOUNT_VALUE = "processing_account";

const AcceptanceCheckbox = ({
  label,
  accepted,
  onToggle,
}: {
  label: string;
  accepted: boolean;
  onToggle: () => void;
}) => (
  <button
    type="button"
    onClick={onToggle}
    className="flex items-center gap-3 group"
  >
    <span
      className={cn(
        "h-5 w-5 rounded border-2 flex items-center justify-center transition-colors shrink-0",
        accepted
          ? "bg-gray-900 border-gray-900"
          : "border-gray-300 group-hover:border-gray-500",
      )}
    >
      {accepted && <Check className="h-3 w-3 text-white" />}
    </span>
    <span className="text-sm text-gray-700">{label}</span>
  </button>
);

const decodeHtml = (encoded: string): string => {
  const el = document.createElement("textarea");
  let current = encoded;
  let prev = "";
  while (current !== prev) {
    prev = current;
    el.innerHTML = current;
    current = el.value;
  }
  return current;
};

const SECTION_TITLE_RE = /^([A-Z][A-Z0-9\s\/\-\,\&\']*[A-Z]\.)\s+\S/;

const promoteListTitles = (html: string): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, "text/html");
  const root = doc.body.firstElementChild as HTMLElement;

  root.querySelectorAll("li").forEach((li) => {
    const text = li.textContent?.trim() ?? "";
    const titleMatch = text.match(SECTION_TITLE_RE);
    if (!titleMatch) return;

    const titleText = titleMatch[1].trim();
    // slice from end of title (group 1) + whitespace, not end of full match
    const bodyText = text.slice(titleMatch[1].length).trim();

    // Title element — bold, no bullet
    const titleEl = doc.createElement("p");
    titleEl.style.marginTop = "0.75rem";
    titleEl.style.fontWeight = "700";
    titleEl.textContent = titleText;

    // Body element — normal weight, below title
    const bodyEl = doc.createElement("p");
    bodyEl.style.marginTop = "0.25rem";
    bodyEl.textContent = bodyText;

    const ul = li.parentElement;
    ul?.parentElement?.insertBefore(titleEl, ul);
    ul?.parentElement?.insertBefore(bodyEl, ul);
    li.remove();
    if (ul && ul.children.length === 0) ul.remove();
  });

  // Convert any remaining <li> (no title) to <p> outside the <ul>
  root.querySelectorAll("li").forEach((li) => {
    const p = doc.createElement("p");
    p.style.marginTop = "0.5rem";
    p.innerHTML = li.innerHTML;
    const ul = li.parentElement;
    ul?.parentElement?.insertBefore(p, ul);
    li.remove();
    if (ul && ul.children.length === 0) ul.remove();
  });

  return root.innerHTML;
};

const FeesLoadingSkeleton = () => (
  <div className="px-8 py-6 space-y-6 animate-pulse">
    <div className="h-4 w-28 bg-gray-200 rounded" />
    {[
      [14, 20, 12],
      [18, 12, 16, 10],
      [16, 64, 12, 12, 12],
      [14, 12, 12, 12, 12],
    ].map((lines, i) => (
      <div key={i} className="space-y-2">
        <div
          className={`h-3.5 bg-gray-300 rounded`}
          style={{ width: `${lines[0]}%` }}
        />
        {lines.slice(1).map((w, j) => (
          <div
            key={j}
            className="h-3 bg-gray-200 rounded"
            style={{ width: `${w}%` }}
          />
        ))}
      </div>
    ))}
  </div>
);

const FeesDisplay = ({ sections }: { sections: FeeSection[] }) => (
  <div className="px-8 py-6 space-y-5">
    <h3 className="text-base font-bold text-gray-900">Rates &amp; Fees</h3>
    {sections.map((section) => (
      <div key={section.section_name} className="space-y-1">
        <p className="text-sm font-bold text-gray-900">
          {section.section_description}
        </p>
        {section.fields.map((field) => (
          <p key={field.label} className="text-sm text-gray-700">
            {field.label} {field.value}
          </p>
        ))}
        {section.disclaimer && (
          <p
            className="text-sm text-gray-700 leading-relaxed pt-1"
            dangerouslySetInnerHTML={{
              __html: decodeHtml(section.disclaimer),
            }}
          />
        )}
      </div>
    ))}
  </div>
);

const FundingTab = ({
  feeAccount,
  onFeeAccountChange,
  paymentMethods,
}: {
  feeAccount: string;
  onFeeAccountChange: (val: string) => void;
  paymentMethods: { id: string; label: string }[];
}) => (
  <div className="space-y-6">
    <div>
      <h3 className="text-sm font-bold text-gray-900 mb-1">
        Bank Funding Account
      </h3>
      <p className="text-sm text-gray-600 leading-relaxed">
        Your batch deposits from card transactions will be deposited into this
        processing account. Batch deposits will show up in Transactions.
        Individual card payments can be found in Get Paid &gt; Customer
        Payments.
      </p>
    </div>

    <div>
      <h3 className="text-sm font-bold text-gray-900 mb-1">Fee Account</h3>
      <p className="text-sm text-gray-600 leading-relaxed mb-3">
        Select the account from which you would like applicable merchant fees to
        be withdrawn.
      </p>
      <Select value={feeAccount} onValueChange={onFeeAccountChange}>
        <SelectTrigger className="max-w-sm">
          <SelectValue placeholder="Select bank account" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={PROCESSING_ACCOUNT_VALUE}>
            Processing Account (this account)
          </SelectItem>
          {paymentMethods.map((pm) => (
            <SelectItem key={pm.id} value={pm.id}>
              {pm.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  </div>
);

const DownloadPdfButton = ({
  userId,
  applicationId,
  providerType,
}: {
  userId: string;
  applicationId: string;
  providerType: string;
}) => {
  const [enabled, setEnabled] = useState(false);
  const pendingDownload = useRef(false);

  const { data, isFetching, isError } = useDocuments(
    userId,
    applicationId,
    providerType,
    {
      enabled,
    },
  );

  const triggerDownload = (url: string) => {
    const byteChars = atob(url);
    const bytes = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++)
      bytes[i] = byteChars.charCodeAt(i);
    const blob = new Blob([bytes], { type: "application/pdf" });
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = "application.pdf";
    a.click();
    URL.revokeObjectURL(blobUrl);
  };

  useEffect(() => {
    if (!data?.url || !pendingDownload.current) return;
    pendingDownload.current = false;
    triggerDownload(data.url);
    setEnabled(false);
  }, [data?.url]);

  useEffect(() => {
    if (!isError) return;
    pendingDownload.current = false;
    toast.error("Failed to download document. Please try again.");
    setEnabled(false);
  }, [isError]);

  const handleClick = () => {
    if (data?.url) {
      triggerDownload(data.url);
      return;
    }
    pendingDownload.current = true;
    setEnabled(true);
  };

  return (
    <Button variant="outline" onClick={handleClick} disabled={isFetching}>
      {isFetching ? (
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
      ) : (
        <Download className="h-4 w-4 mr-2" />
      )}
      {isFetching ? "Downloading..." : "Download Application PDF"}
    </Button>
  );
};

const TermsTab = ({
  sections,
  acceptedSections,
  onToggleSection,
  activeSection,
  onSectionChange,
  termsAlreadyAccepted,
  userId,
  applicationId,
  providerType,
}: {
  sections: {
    key: string;
    label: string;
    html: string;
    requiresConsent: boolean;
  }[];
  acceptedSections: Record<string, boolean>;
  onToggleSection: (key: string) => void;
  activeSection: string;
  onSectionChange: (key: string) => void;
  termsAlreadyAccepted: boolean;
  userId: string;
  applicationId: string;
  providerType: string;
}) => {
  const currentIndex = sections.findIndex((s) => s.key === activeSection);
  const currentSection = sections[currentIndex];

  if (sections.length === 0) {
    return <p className="text-sm text-gray-400">Loading terms...</p>;
  }

  return (
    <div className="flex flex-col flex-1 min-h-0 gap-4">
      <div className="flex gap-0 border-b border-gray-200 overflow-x-auto shrink-0">
        {sections.map(({ key, label, requiresConsent }, index) => {
          const isActive = key === activeSection;
          const prevSection = sections[index - 1];
          // Submit Application tab (non-consent) only unlocks when API confirms terms accepted
          const isEnabled =
            index === 0 ||
            (!requiresConsent
              ? termsAlreadyAccepted
              : prevSection.requiresConsent
              ? acceptedSections[prevSection.key]
              : true);
          return (
            <button
              key={key}
              type="button"
              disabled={!isEnabled}
              onClick={() => isEnabled && onSectionChange(key)}
              className={cn(
                "whitespace-nowrap px-3 py-2 text-xs font-medium border-b-2 transition-colors",
                isActive
                  ? "border-gray-900 text-gray-900"
                  : isEnabled
                  ? "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  : "border-transparent text-gray-300 cursor-not-allowed",
              )}
            >
              {label}
            </button>
          );
        })}
      </div>

      <div
        className="flex-1 min-h-0 overflow-y-auto rounded-lg border border-gray-200 p-6 text-sm text-gray-700 leading-relaxed [&_b]:!font-bold [&_strong]:!font-bold [&_ul]:list-disc [&_ul]:pl-5 [&_li]:mt-1 [&_p]:mt-2"
        dangerouslySetInnerHTML={{
          __html: promoteListTitles(decodeHtml(currentSection?.html ?? "")),
        }}
      />

      {currentSection?.requiresConsent && (
        <AcceptanceCheckbox
          label={`I agree to the ${currentSection.label} terms referenced above.`}
          accepted={acceptedSections[activeSection] ?? false}
          onToggle={() => onToggleSection(activeSection)}
        />
      )}

      {!currentSection?.requiresConsent && (
        <div>
          <DownloadPdfButton
            userId={userId}
            applicationId={applicationId}
            providerType={providerType}
          />
        </div>
      )}
    </div>
  );
};

const Step4 = () => {
  const history = useHistory();
  const applicationId = useApplicationId();
  const { cognitoId, clientId, userRole } = useAuth();
  const userId =
    userRole === "admin" || userRole === "auth_user" ? clientId : cognitoId;

  const { data: applicationInfo } = useApplicationInfo(
    userId || "",
    PROVIDER_TYPE,
  );
  const appStatus = applicationInfo?.status;

  const feesAlreadyAccepted =
    appStatus === APPLICATION_STATUS.ACCEPTED_FEES ||
    appStatus === APPLICATION_STATUS.ACCEPT_TERMS_AND_CONDITIONS ||
    appStatus === APPLICATION_STATUS.ACCEPTED_TERMS_AND_CONDITIONS ||
    appStatus === APPLICATION_STATUS.APPLICATION_SUBMITTED;

  const termsAlreadyAccepted =
    appStatus === APPLICATION_STATUS.ACCEPTED_TERMS_AND_CONDITIONS ||
    appStatus === APPLICATION_STATUS.APPLICATION_SUBMITTED;

  const { data: feesData, isLoading: feesLoading } = useFees(
    userId || "",
    applicationId,
    PROVIDER_TYPE,
    { enabled: !!userId && !!applicationId },
  );

  const { data: termsData } = useTermsAndConditions(
    userId || "",
    applicationId,
    PROVIDER_TYPE,
    { enabled: !!userId && !!applicationId && feesAlreadyAccepted },
  );

  const consentSections = (termsData ?? [])
    .filter((s) => s.is_consent_required)
    .map((s) => ({
      key: s.property_name,
      label: extractTitle(s.agreement) || s.property_name,
      html: s.agreement,
      requiresConsent: true,
    }));

  const submitSection = (termsData ?? []).find((s) => !s.is_consent_required);
  const termsSections = submitSection
    ? [
        ...consentSections,
        {
          key: submitSection.property_name,
          label: "Submit Application",
          html: submitSection.agreement,
          requiresConsent: false,
        },
      ]
    : consentSections;

  const { mutate: acceptFeesMutation, isPending: isAcceptingFees } =
    useAcceptFees({
      onError: () => toast.error("Failed to accept fees. Please try again."),
    });

  const { mutate: acceptTermsMutation, isPending: isAcceptingTerms } =
    useAcceptTermsAndConditions({
      onError: () => toast.error("Failed to accept terms. Please try again."),
    });

  const { mutate: submitApplicationMutation, isPending: isSubmitting } =
    useSubmitApplication({
      onSuccess: () => {
        if (userId) localStorage.removeItem(`pb_fee_account_${userId}`);
        history.push(getMerchantStepPath(5));
      },
      onError: () =>
        toast.error("Failed to submit application. Please try again."),
    });

  const [activeTab, setActiveTab] = useState<TopTab>("rates");
  const [ratesAccepted, setRatesAccepted] = useState(false);
  const [feeAccount, setFeeAccount] = useState("");
  const [activeTermsSection, setActiveTermsSection] = useState<string>("");
  const [acceptedSections, setAcceptedSections] = useState<
    Record<string, boolean>
  >({});

  useEffect(() => {
    if (feeAccount && userId) saveFeeAccountToStorage(userId, feeAccount);
  }, [feeAccount, userId]);

  useEffect(() => {
    if (!appStatus) return;

    const storedFeeAccount = getFeeAccountFromStorage(userId || "");

    if (feesAlreadyAccepted) {
      setRatesAccepted(true);
      if (storedFeeAccount) setFeeAccount(storedFeeAccount);
    }

    if (termsAlreadyAccepted && termsSections.length > 0) {
      const allAccepted = termsSections.reduce<Record<string, boolean>>(
        (acc, s) => {
          if (s.requiresConsent) acc[s.key] = true;
          return acc;
        },
        {},
      );
      setAcceptedSections(allAccepted);

      if (storedFeeAccount) {
        setActiveTab("terms");
        const submitSection = termsSections.find((s) => !s.requiresConsent);
        if (submitSection) setActiveTermsSection(submitSection.key);
      }
    } else if (feesAlreadyAccepted && storedFeeAccount) {
      setActiveTab("terms");
    } else if (feesAlreadyAccepted) {
      setActiveTab("funding");
    }
  }, [appStatus, termsSections.length]);

  const { data: paymentMethodsData } = usePaymentMethods(userId || "");
  const paymentMethods = (paymentMethodsData ?? [])
    .filter((pm) => pm.account_type !== "Credit Card")
    .map((pm) => ({
      id: pm.payment_method_id,
      label:
        pm.payment_method_nickname ||
        pm.payment_method_data?.name_on_account ||
        pm.payment_method_id,
    }));

  const currentTermsSection =
    activeTermsSection ||
    (termsSections.length > 0 ? termsSections[0].key : "");
  const currentTermsIndex = termsSections.findIndex(
    (s) => s.key === currentTermsSection,
  );
  const isLastTermsSection =
    termsSections.length > 0 && currentTermsIndex === termsSections.length - 1;

  const allTermsAccepted =
    consentSections.length > 0 &&
    consentSections.every((s) => acceptedSections[s.key]);
  const allAccepted = ratesAccepted && !!feeAccount && allTermsAccepted;

  const toggleSection = (key: string) => {
    setAcceptedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const getNextLabel = () => {
    if (activeTab === "rates") return isAcceptingFees ? "Saving..." : undefined;
    if (activeTab === "funding") return undefined;

    if (isLastTermsSection)
      return isSubmitting ? "Submitting..." : "Accept & Submit Application";
    if (isAcceptingTerms) return "Saving...";
    return "Accept & Continue";
  };

  const getNextDisabled = () => {
    if (activeTab === "rates")
      return !feesAlreadyAccepted && (!ratesAccepted || isAcceptingFees);
    if (activeTab === "funding") return !feeAccount;
    if (isAcceptingTerms || isSubmitting) return true;
    const currentSection = termsSections.find(
      (s) => s.key === currentTermsSection,
    );

    if (!currentSection) return true;
    if (!currentSection.requiresConsent) {
      if (consentSections.length === 0) return true;
      return !allTermsAccepted && !termsAlreadyAccepted;
    }
    if (termsAlreadyAccepted) return false;
    return !acceptedSections[currentTermsSection];
  };

  const lastConsentIndex = [...termsSections]
    .reverse()
    .findIndex((s) => s.requiresConsent);
  const lastConsentSectionIndex =
    lastConsentIndex === -1 ? -1 : termsSections.length - 1 - lastConsentIndex;
  const isLastConsentSection = currentTermsIndex === lastConsentSectionIndex;

  const handleNext = () => {
    if (activeTab === "rates") {
      if (feesAlreadyAccepted) {
        setActiveTab("funding");
      } else {
        acceptFeesMutation(
          {
            userId: userId || "",
            applicationId,
            providerType: PROVIDER_TYPE,
            payload: { accepted: true },
          },
          { onSuccess: () => setActiveTab("funding") },
        );
      }
    } else if (activeTab === "funding") {
      setActiveTab("terms");
    } else {
      if (!isLastTermsSection) {
        if (isLastConsentSection) {
          if (termsAlreadyAccepted) {
            setActiveTermsSection(termsSections[currentTermsIndex + 1].key);
          } else {
            acceptTermsMutation(
              {
                userId: userId || "",
                applicationId,
                providerType: PROVIDER_TYPE,
                payload: { accepted: true },
              },
              {
                onSuccess: () =>
                  setActiveTermsSection(
                    termsSections[currentTermsIndex + 1].key,
                  ),
              },
            );
          }
        } else {
          setActiveTermsSection(termsSections[currentTermsIndex + 1].key);
        }
      } else {
        if (consentSections.length === 0) {
          toast.error("Terms & Conditions did not load properly. Please refresh and try again.");
          return;
        }
        if (!allTermsAccepted && !termsAlreadyAccepted) {
          toast.error("Please accept all Terms & Conditions before submitting.");
          return;
        }
        fetch("https://api.ipify.org?format=json")
          .then((res) => res.json())
          .then(({ ip }: { ip: string }) => {
            submitApplicationMutation({
              userId: userId || "",
              applicationId,
              providerType: PROVIDER_TYPE,
              payload: {
                ip_address: ip,
                device_info: navigator.userAgent,
                browser_info: navigator.userAgent,
                ...(feeAccount !== PROCESSING_ACCOUNT_VALUE && {
                  fee_account: { payment_method_id: feeAccount },
                }),
                comment: "Submitting application from Pocketbook application",
              },
            });
          })
          .catch(() => {
            toast.error("Failed to retrieve network info. Please try again.");
          });
      }
    }
  };

  const handleBack = () => {
    if (activeTab === "funding") {
      setActiveTab("rates");
    } else if (activeTab === "terms") {
      if (currentTermsIndex > 0) {
        setActiveTermsSection(termsSections[currentTermsIndex - 1].key);
      } else {
        setActiveTab("funding");
      }
    } else {
      history.push(MERCHANT_EXIT_PATH);
    }
  };

  return (
    <StepLayout
      step={4}
      onNext={handleNext}
      onBack={handleBack}
      nextLabel={getNextLabel()}
      nextDisabled={getNextDisabled()}
      isLoading={isAcceptingFees || isAcceptingTerms || isSubmitting}
      wide
      fullHeight
    >
      <div className="flex border-b border-gray-200 -mx-8 -mt-8">
        {TOP_TABS.map(({ key, label }) => (
          <div
            key={key}
            className={cn(
              "flex-1 flex flex-col items-center justify-center px-4 py-3.5 text-xs font-semibold text-center first:rounded-tl-xl last:rounded-tr-xl select-none leading-tight transition-colors border-b-2 -mb-px",
              activeTab === key
                ? "border-skin-primary text-skin-primary bg-white"
                : "border-transparent text-gray-400 bg-gray-50 hover:text-gray-600",
            )}
          >
            {label.map((line, i) => (
              <span key={i}>{line}</span>
            ))}
          </div>
        ))}
      </div>

      {activeTab === "rates" && (
        <>
          <div className="flex-1 overflow-y-auto -mx-8 -mt-5 min-h-0">
            {feesLoading ? (
              <FeesLoadingSkeleton />
            ) : feesData && feesData.length > 0 ? (
              <FeesDisplay sections={feesData} />
            ) : (
              <p className="text-sm text-gray-400 px-8 pt-5">
                No rates & fees information available.
              </p>
            )}
          </div>
          <AcceptanceCheckbox
            label="I have read and accept the Rates & Fees"
            accepted={ratesAccepted}
            onToggle={() => setRatesAccepted((v) => !v)}
          />
        </>
      )}

      {activeTab === "funding" && (
        <FundingTab
          feeAccount={feeAccount}
          onFeeAccountChange={setFeeAccount}
          paymentMethods={paymentMethods}
        />
      )}

      {activeTab === "terms" && (
        <TermsTab
          sections={termsSections}
          acceptedSections={acceptedSections}
          onToggleSection={toggleSection}
          activeSection={currentTermsSection}
          onSectionChange={setActiveTermsSection}
          termsAlreadyAccepted={termsAlreadyAccepted}
          userId={userId || ""}
          applicationId={applicationId}
          providerType={PROVIDER_TYPE}
        />
      )}

      {!allAccepted && (
        <p className="text-xs text-gray-400">
          {(ratesAccepted ? 1 : 0) +
            (feeAccount ? 1 : 0) +
            (allTermsAccepted ? 1 : 0)}{" "}
          of 3 sections completed
        </p>
      )}
    </StepLayout>
  );
};

export default Step4;
